/* eslint-disable react/display-name */
import MaterialTable from '@material-table/core'
import { Dialog, DialogActions, DialogContent, Paper } from '@material-ui/core'
import { LocalizedButton } from 'components/Localization/LocalizedButton'
import { useMaterialTableLocalization } from 'customHooks/useMaterialTableLocalization'
import { newProjectActions } from 'features/BillOfMaterials/store/newProjectReducer'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import { useAppDispatch } from 'store/configureStore'
import { IssueDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { APICall, ErrorMessage } from 'store/Application/appActions'
import { RootAppState } from 'store/configureStore'
import { useIsLoading } from 'utils/useOrganizationContext'
import './IssuesDialog.scss'

export type IssuesDialogProps = {
  itemId: string
  open: boolean
  onClose: () => void
}

export const IssuesDialog = (props: IssuesDialogProps) => {
  const { t } = useTranslation()
  const materialTableLocalization = useMaterialTableLocalization()

  const [issueList, setIssueList] = useState<IssueDto[]>([])

  const flatView = useAppSelector(
    (state: RootAppState) => state.project.flatView
  )

  const { organizationId, projectId, networkLabel, isLoading } =
    useIsLoading('get-progress')

  const dispatch = useAppDispatch()

  useEffect(() => {
    const url =
      props.itemId === projectId
        ? `api/parties/${organizationId}/projects/${projectId}/issues`
        : `api/parties/${organizationId}/projects/${projectId}/items/${props.itemId}/issues`

    dispatch(
      APICall({
        label: networkLabel,
        configuration: {
          method: 'GET',
          url,
          onSuccess: (data: IssueDto[]) => {
            setIssueList(data)
          },
          onError: (message) => {
            ErrorMessage('progress', message)
          },
        },
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setValidationHightlight = (rowId) => {
    // dispatch(oldProjectActions.RemoveAllValidationHightlight())
    dispatch(
      newProjectActions.setOpen({
        bomItemId: rowId,
        isOpen: true,
      })
    )

    dispatch(newProjectActions.setBomItemInSearch(rowId))
    props.onClose()
  }

  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="xl">
      <DialogContent style={{ height: '60vh', padding: 0 }}>
        <MaterialTable
          isLoading={isLoading}
          title={t('project:progress-modal--title', 'issues')}
          options={{
            emptyRowsWhenPaging: false,
            paging: issueList?.length > 10,
            pageSize: 10,
            pageSizeOptions: [10, 20, 30],
            showFirstLastPageButtons: true,
            paginationType: 'stepped',
            exportAllData: false,
            actionsColumnIndex: -1,
            draggable: true,
            grouping: true,
            maxBodyHeight: '44.5vh',
            headerStyle: {
              fontWeight: 'bold',
            },
            showTitle: true,
            defaultOrderByCollection: []
          }}
          localization={materialTableLocalization}
          columns={[
            {
              title: t('common:row-number', 'row number'),
              grouping: true,
              cellStyle: { textAlign: 'center', paddingRight: '40px' },
              headerStyle: {
                textAlign: 'center',
                width: '2%',
              },
              render: (data) =>
                flatView[data.itemId] && flatView[data.itemId]['rowNumber'],
            },
            {
              title: t('common:severity'),
              field: 'severityDescription',
              grouping: true,
            },
            {
              title: t('common:category'),
              field: 'categoryDescription',
              grouping: true,
            },
            {
              title: t('common:description'),
              field: 'description',
              grouping: false,
            },
            {
              title: t('common:type'),
              field: 'typeDescription',
              grouping: true,
            },
            {
              title: t('common:date'),
              field: 'timestamp',
              grouping: false,
              type: 'date',
            },
          ]}
          data={issueList}
          onRowClick={(ev, rowData) => {
            setValidationHightlight(rowData.itemId)
          }}
          components={{
            Container: (props) => <Paper {...props} elevation={0} />,
          }}
        />
      </DialogContent>
      <DialogActions>
        <LocalizedButton
          translationKey="common:close"
          onClick={props.onClose}
        />
      </DialogActions>
    </Dialog>
  )
}
